import { create } from 'zustand';
import { errorMessage } from '../lib/errors.js';
import { login as apiLogin } from '../api/auth.js';
import { getToken, setToken, clearToken, setUnauthorizedHandler } from '../api/client.js';
import { clearTokenInShell, shareTokenWithShell } from '../lib/native.js';

interface AuthState {
  token: string | null;
  error: string | null;
  loading: boolean;
  login: (password: string) => Promise<void>;
  logout: () => void;
  /** Session expirée (401) : comme logout, sans couper les notifications de l'appareil. */
  expire: () => void;
}

export const useAuth = create<AuthState>((set) => ({
  token: getToken(),
  error: null,
  loading: false,
  login: async (password) => {
    set({ loading: true, error: null });
    try {
      const { token } = await apiLogin(password);
      setToken(token);
      shareTokenWithShell(token);
      set({ token, loading: false });
    } catch (err) {
      set({
        error: errorMessage(err, 'Login failed'),
        loading: false,
      });
      throw err;
    }
  },
  logout: () => {
    clearToken();
    clearTokenInShell({ disablePush: true });
    set({ token: null });
  },
  expire: () => {
    clearToken();
    clearTokenInShell({ disablePush: false });
    set({ token: null });
  },
}));

// Un 401 sur un appel authentifié ferme la session, comme un logout — sans désabonner le push.
setUnauthorizedHandler(() => useAuth.getState().expire());

// Dans la coque Android, le natif reçoit le jeton à chaque démarrage : une réinstallation de
// l'APK ou un stockage natif effacé se réparent à la première ouverture.
const tokenAtStartup = getToken();
if (tokenAtStartup) shareTokenWithShell(tokenAtStartup);
